import { useMutation, useQuery } from "@tanstack/react-query";
import {
  CreateFolderDto,
  DeleteFolderResponse,
  FolderDto,
  MoveFolderDto,
  RenameFolderDto,
} from "@repo/api/folder";
import { VideoDto, VideoSort } from "@repo/api/video";
import { FolderApi } from "../api/folder-api";
import { VideoApi } from "../api/video-api";
import { queryClient } from "./query-client";
import Toast from "../components/Toast";

export function useFolderContentsQuery(
  teamExternalId: string,
  folderExternalId?: string,
  sort: VideoSort = "lastUpdated",
) {
  return useQuery({
    queryKey: folderQueryKeys.contents(
      teamExternalId,
      folderExternalId ?? null,
      sort,
    ),
    queryFn: () =>
      FolderApi.getFolderContents(teamExternalId, folderExternalId, sort),
  });
}

async function invalidateFolders(teamExternalId: string) {
  await queryClient.invalidateQueries({
    queryKey: folderQueryKeys.all(teamExternalId),
  });
}

async function invalidateFoldersAndVideos(teamExternalId: string) {
  await Promise.all([
    invalidateFolders(teamExternalId),
    queryClient.invalidateQueries({ queryKey: ["video"] }),
  ]);
}

export function useCreateFolderMutation({
  teamExternalId,
  onSuccess,
  onError,
}: {
  teamExternalId: string;
  onSuccess?: (data: FolderDto) => void;
  onError?: (error: Error) => void;
}) {
  return useMutation({
    mutationFn: (data: CreateFolderDto) =>
      FolderApi.createFolder(teamExternalId, data),
    onSuccess: async (data) => {
      await invalidateFolders(teamExternalId);
      onSuccess?.(data);
    },
    onError: (error) => {
      Toast.error("Failed to create folder");
      onError?.(error);
    },
  });
}

export function useRenameFolderMutation({
  teamExternalId,
  onSuccess,
  onError,
}: {
  teamExternalId: string;
  onSuccess?: (data: FolderDto) => void;
  onError?: (error: Error) => void;
}) {
  return useMutation({
    mutationFn: ({
      folderExternalId,
      data,
    }: {
      folderExternalId: string;
      data: RenameFolderDto;
    }) => FolderApi.renameFolder(teamExternalId, folderExternalId, data),
    onSuccess: async (data) => {
      await invalidateFolders(teamExternalId);
      onSuccess?.(data);
    },
    onError: (error) => {
      Toast.error("Failed to rename folder");
      onError?.(error);
    },
  });
}

export function useMoveFolderMutation({
  teamExternalId,
  onSuccess,
  onError,
}: {
  teamExternalId: string;
  onSuccess?: (data: FolderDto) => void;
  onError?: (error: Error) => void;
}) {
  return useMutation({
    mutationFn: ({
      folderExternalId,
      data,
    }: {
      folderExternalId: string;
      data: MoveFolderDto;
    }) => FolderApi.moveFolder(teamExternalId, folderExternalId, data),
    onSuccess: async (data) => {
      await invalidateFolders(teamExternalId);
      onSuccess?.(data);
    },
    onError: (error) => {
      Toast.error("Failed to move folder");
      onError?.(error);
    },
  });
}

export function useDeleteFolderMutation({
  teamExternalId,
  onSuccess,
  onError,
}: {
  teamExternalId: string;
  onSuccess?: (data: DeleteFolderResponse) => void;
  onError?: (error: Error) => void;
}) {
  return useMutation({
    mutationFn: (folderExternalId: string) =>
      FolderApi.deleteFolder(teamExternalId, folderExternalId),
    onSuccess: async (data) => {
      await invalidateFoldersAndVideos(teamExternalId);
      onSuccess?.(data);
    },
    onError: (error) => {
      Toast.error("Failed to delete folder");
      onError?.(error);
    },
  });
}

export function useMoveVideoToFolderMutation({
  teamExternalId,
  onSuccess,
  onError,
}: {
  teamExternalId: string;
  onSuccess?: (data: VideoDto) => void;
  onError?: (error: Error) => void;
}) {
  return useMutation({
    mutationFn: ({
      videoExternalId,
      folderExternalId,
    }: {
      videoExternalId: string;
      /** null moves the video out of its folder. */
      folderExternalId: string | null;
    }) =>
      VideoApi.moveVideoToFolder(teamExternalId, videoExternalId, {
        folderExternalId,
      }),
    onSuccess: async (data) => {
      await invalidateFoldersAndVideos(teamExternalId);
      onSuccess?.(data);
    },
    onError: (error) => {
      Toast.error("Failed to move video");
      onError?.(error);
    },
  });
}

export const folderQueryKeys = {
  all: (teamExternalId: string) => ["folder", { teamExternalId }],
  contents: (
    teamExternalId: string,
    folderExternalId: string | null,
    sort: VideoSort,
  ) => ["folder", { teamExternalId }, "contents", { folderExternalId, sort }],
};
